'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { loginWithUsernamePin, setPosSession, isCashierRole } from '@/lib/posAuth';

export default function LoginPageClient() {
  const router = useRouter();
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [step, setStep] = useState<'username' | 'pin'>('username');

  const handleUsernameNext = useCallback(() => {
    if (!username.trim()) {
      setError('Please enter your username.');
      return;
    }
    setError('');
    setStep('pin');
  }, [username]);

  const handlePinInput = useCallback((digit: string) => {
    if (pin.length >= 4) return;
    const newPin = pin + digit;
    setPin(newPin);
    setError('');
    if (newPin.length === 4) {
      handleSubmit(newPin);
    }
  }, [pin]);

  const handleClear = useCallback(() => {
    setPin('');
    setError('');
  }, []);

  const handleBack = useCallback(() => {
    setStep('username');
    setPin('');
    setError('');
  }, []);

  const handleSubmit = useCallback(async (enteredPin: string) => {
    setIsLoading(true);
    try {
      const user = await loginWithUsernamePin(username, enteredPin);
      if (user) {
        setPosSession(user);
        setTimeout(() => {
          if (isCashierRole(user.role)) {
            router.push('/sales-screen');
          } else {
            router.push('/admin-dashboard');
          }
        }, 200);
      } else {
        setIsLoading(false);
        setPin('');
        setError('Invalid username or PIN — please try again');
        setShake(true);
        setTimeout(() => setShake(false), 500);
      }
    } catch {
      setIsLoading(false);
      setPin('');
      setError('Authentication error — please try again');
    }
  }, [router, username]);

  return (
    <div className="relative min-h-screen w-full flex overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('/assets/images/image-1784901293513.png')` }}
      />
      <div className="absolute inset-0" style={{ background: 'rgba(20, 12, 0, 0.72)' }} />

      {/* Left — Branding */}
      <div className="relative z-10 flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center" style={{ width: 72, height: 72 }}>
              <svg viewBox="0 0 72 72" width="72" height="72" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 56 L20 16 L44 16 C54 16 60 22 60 30 C60 38 54 44 44 44 L20 44" stroke="white" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                <path d="M14 62 C18 54 24 46 32 40 C26 50 28 58 36 62" fill="#f97316"/>
                <path d="M32 40 C36 32 44 28 50 32 C44 36 40 44 36 62" fill="#f97316" opacity="0.7"/>
              </svg>
            </div>
            <div>
              <p className="font-black tracking-widest text-white" style={{ fontSize: 42, letterSpacing: '0.08em', lineHeight: 1 }}>
                SUPER<span style={{ color: '#f97316' }}>POS</span>
              </p>
              <p className="text-white/60 text-sm tracking-widest uppercase mt-1">Professional Retail System</p>
            </div>
          </div>
          <div className="mt-6 text-center">
            <p className="text-white/40 text-xs tracking-wider uppercase">Secure PIN Authentication</p>
          </div>
        </div>
      </div>

      {/* Right — Login Panel */}
      <div className="relative z-10 flex items-center justify-center" style={{ width: 440, minWidth: 380 }}>
        <div className={`w-full px-8 ${shake ? 'animate-bounce' : ''}`}>

          {step === 'username' ? (
            /* ── Step 1: Username ── */
            <div>
              <h2 className="text-white text-2xl font-semibold text-center mb-2" style={{ letterSpacing: '0.02em' }}>
                Welcome Back
              </h2>
              <p className="text-white/50 text-sm text-center mb-8">Enter your username to continue</p>

              <div className="mb-4">
                <label className="block text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={e => { setUsername(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleUsernameNext()}
                  placeholder="Enter your username"
                  autoFocus
                  className="w-full h-12 px-4 rounded-xl text-white text-sm placeholder-white/30 focus:outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1.5px solid rgba(255,255,255,0.2)',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                />
              </div>

              {error && (
                <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/20 border border-red-400/30">
                  <p className="text-xs text-red-200 font-medium text-center">{error}</p>
                </div>
              )}

              <button
                onClick={handleUsernameNext}
                className="w-full h-12 rounded-xl text-white text-sm font-bold transition-all duration-150 active:scale-95 mt-2"
                style={{ background: '#f97316' }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#ea6c0a'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f97316'; }}
              >
                Continue
              </button>

            </div>
          ) : (
            /* ── Step 2: PIN ── */
            <div>
              <button
                onClick={handleBack}
                className="flex items-center gap-1.5 text-white/50 text-xs hover:text-white/80 transition-colors mb-6"
              >
                ← Back
              </button>

              <h2 className="text-white text-2xl font-semibold text-center mb-1" style={{ letterSpacing: '0.02em' }}>
                Enter Your PIN
              </h2>
              <p className="text-white/50 text-sm text-center mb-6">
                Logging in as <span className="text-white/80 font-semibold">{username}</span>
              </p>

              {/* PIN indicator boxes */}
              <div className="grid grid-cols-4 gap-3 mb-6">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={`pin-box-${i}`}
                    className="rounded-lg transition-all duration-200 flex items-center justify-center"
                    style={{
                      height: 64,
                      border: i < pin.length ? '2px solid rgba(255,255,255,0.9)' : '2px solid rgba(255,255,255,0.35)',
                      background: i < pin.length ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.07)',
                    }}
                  >
                    {i < pin.length && (
                      showPin
                        ? <span className="text-white text-xl font-bold">{pin[i]}</span>
                        : <span className="w-3 h-3 rounded-full bg-white inline-block" />
                    )}
                  </div>
                ))}
              </div>

              {/* Show/hide PIN toggle */}
              <div className="flex justify-end mb-3">
                <button
                  onClick={() => setShowPin(v => !v)}
                  className="flex items-center gap-1 text-white/40 text-xs hover:text-white/70 transition-colors"
                >
                  {showPin ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showPin ? 'Hide' : 'Show'} PIN
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 px-3 py-2 rounded-lg bg-red-500/20 border border-red-400/30">
                  <p className="text-xs text-red-200 font-medium text-center">{error}</p>
                </div>
              )}

              {/* Numpad rows 1-3 */}
              <div className="grid grid-cols-3 gap-3 mb-3">
                {['1','2','3','4','5','6','7','8','9'].map((key) => (
                  <button
                    key={`numpad-${key}`}
                    onClick={() => handlePinInput(key)}
                    disabled={isLoading || pin.length >= 4}
                    className="flex items-center justify-center text-white text-2xl font-semibold rounded-lg transition-all duration-150 active:scale-95"
                    style={{
                      height: 72,
                      border: '2px solid rgba(255,255,255,0.55)',
                      background: 'rgba(255,255,255,0.06)',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.18)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; }}
                    aria-label={`PIN digit ${key}`}
                  >
                    {key}
                  </button>
                ))}
              </div>

              {/* Bottom row */}
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={handleClear}
                  disabled={isLoading}
                  className="flex items-center justify-center text-white text-sm font-bold rounded-lg transition-all duration-150 active:scale-95"
                  style={{ height: 72, border: '2px solid rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.06)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.18)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; }}
                  aria-label="Clear PIN"
                >
                  CLEAR
                </button>
                <button
                  onClick={() => handlePinInput('0')}
                  disabled={isLoading || pin.length >= 4}
                  className="flex items-center justify-center text-white text-2xl font-semibold rounded-lg transition-all duration-150 active:scale-95"
                  style={{ height: 72, border: '2px solid rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.06)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.18)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; }}
                  aria-label="PIN digit 0"
                >
                  0
                </button>
                <button
                  onClick={() => pin.length === 4 && handleSubmit(pin)}
                  disabled={isLoading || pin.length < 4}
                  className="flex items-center justify-center text-white text-sm font-bold rounded-lg transition-all duration-150 active:scale-95"
                  style={{
                    height: 72,
                    background: pin.length === 4 && !isLoading ? '#f97316' : 'rgba(249,115,22,0.5)',
                    border: '2px solid transparent',
                  }}
                  onMouseEnter={e => { if (pin.length === 4 && !isLoading) (e.currentTarget as HTMLButtonElement).style.background = '#ea6c0a'; }}
                  onMouseLeave={e => { if (pin.length === 4 && !isLoading) (e.currentTarget as HTMLButtonElement).style.background = '#f97316'; }}
                  aria-label="Login"
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'LOGIN'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}